/**
 * El usado volvio del tecnico. Es el cierre de la orden interna.
 *
 * No se factura ni se cobra nada: lo que costo (tecnico + encomienda) se suma
 * al costo del usado, convertido a su moneda al cambio de hoy. Se muestra
 * antes de apretar: costo actual, cuanto suma, costo nuevo, y el margen que
 * queda con el precio de venta actual. El precio no se mueve solo —los usados
 * suelen tenerlo fijado— asi que se ofrece corregirlo aca mismo.
 *
 * "No se pudo arreglar" cierra la orden sin tocar el costo.
 */
import { useEffect, useMemo, useState } from "react";
import { PackageCheck } from "lucide-react";

import { Importe, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { EntregaOt, Producto } from "@/lib/tipos";

import {
  armarEntrega,
  entregaInicial,
  evaluarCierreInterno,
  nombreOrden,
  type FormularioEntrega,
  type OrdenTaller,
} from "./logica";

export interface DialogVolvioAlStockProps {
  orden: OrdenTaller | null;
  /** La ficha del usado. Hasta que llegue no se puede confirmar: sin ella no hay costo que sumar. */
  producto: Producto | null;
  cotizacion: number;
  guardando?: boolean;
  onCerrar: () => void;
  onConfirmar: (entrega: EntregaOt) => void;
}

export function DialogVolvioAlStock({
  orden,
  producto,
  cotizacion,
  guardando = false,
  onCerrar,
  onConfirmar,
}: DialogVolvioAlStockProps): JSX.Element {
  const [form, setForm] = useState<FormularioEntrega>({
    pagos: [],
    cierre: null,
    cobrarDiagnostico: false,
    precioVenta: null,
  });

  useEffect(() => {
    if (!orden) return;
    setForm({ ...entregaInicial(orden), cobrarDiagnostico: false, precioVenta: null });
  }, [orden]);

  const ev = useMemo(
    () => (orden && producto ? evaluarCierreInterno(orden, producto, form, cotizacion) : null),
    [orden, producto, form, cotizacion],
  );

  if (!orden) {
    return <Dialog open={false} onOpenChange={() => onCerrar()} />;
  }

  const yaSinArreglo = orden.estado === "SIN_ARREGLO";
  const sinArreglo = ev?.sinArreglo ?? yaSinArreglo;

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="md">
        <DialogHeader>
          <DialogTitle>Volvió al stock</DialogTitle>
          <DialogDescription>
            {nombreOrden(orden)} · {producto?.nombre ?? orden.equipo}. No se factura nada: lo que
            costó se suma al costo del equipo.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {!yaSinArreglo ? (
            <Card variant="muted" padding="compacto">
              <Checkbox
                label="No se pudo arreglar"
                ayuda="Se cierra la orden y el costo del equipo no cambia."
                checked={form.cierre === "SIN_ARREGLO"}
                onChange={(e) =>
                  setForm((f) => ({ ...f, cierre: e.target.checked ? "SIN_ARREGLO" : null }))
                }
              />
            </Card>
          ) : null}

          {!producto ? (
            <Cargando texto="Cargando la ficha del equipo…" tamano="inline" />
          ) : sinArreglo ? (
            <Aviso tono="neutral" compacto>
              Vuelve sin arreglo. No se toca el costo del equipo.
            </Aviso>
          ) : ev ? (
            <>
              <Card variant="muted" padding="compacto">
                <dl className="space-y-1.5 text-sm">
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted-foreground">Costo actual</dt>
                    <dd>
                      <Importe monto={ev.costoAnterior} moneda={ev.moneda} />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4">
                    <dt className="text-muted-foreground">Reacondicionamiento</dt>
                    <dd className="flex items-baseline gap-1">
                      <span aria-hidden="true">+</span>
                      <Importe monto={ev.sumado} moneda={ev.moneda} />
                    </dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5">
                    <dt className="font-medium">Costo nuevo</dt>
                    <dd>
                      <Importe
                        monto={ev.costoNuevo}
                        moneda={ev.moneda}
                        tamano="lg"
                        className="font-semibold"
                      />
                    </dd>
                  </div>
                </dl>
              </Card>

              <InputImporte
                label="Nuevo precio de venta"
                valor={form.precioVenta ?? ev.precioActual}
                onValorChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    precioVenta: v !== null && v === ev.precioActual ? null : v,
                  }))
                }
                moneda={ev.precioMoneda}
                ayuda={
                  producto.precio_manual === null
                    ? "Sin precio fijado: si no escribís uno, el precio se recalcula solo con el costo nuevo."
                    : "Si lo dejás igual, el precio no se toca."
                }
              />
            </>
          ) : null}

          <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
            <PackageCheck className="mt-px size-3 shrink-0" aria-hidden="true" />
            <span>El equipo sigue en stock y queda listo para vender.</span>
          </p>

          {ev && !sinArreglo && ev.yaVendido ? (
            <Aviso tono="warning" compacto>
              Este equipo ya se vendió. El costo del reacondicionamiento queda en el producto, pero
              no entró en esa venta.
            </Aviso>
          ) : null}

          {/* Ultimo hijo del body, arriba del footer. El numero lleva el color
              de su moneda; que pierda lo dice el tono del aviso, no el importe. */}
          {ev && !sinArreglo && producto && producto.precio_manual !== null ? (
            <Aviso tono={ev.margenNuevo < 0 ? "destructive" : "neutral"} compacto>
              Con ese precio queda un margen de{" "}
              <Importe monto={ev.margenNuevo} moneda={ev.moneda} tamano="sm" />.
            </Aviso>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            type="button"
            cargando={guardando}
            disabled={!producto}
            onClick={() => onConfirmar(armarEntrega(orden, form))}
          >
            Volvió al stock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
